/**
 * bottomStack — the measured height of the selection bar, so the coach pills
 * stacked above it can sit on top of it instead of on a guessed constant.
 *
 * The bar is a DS `ActionBar` (viewport-fixed, bottom-centre) whose height
 * now varies: one row of verbs on a wide screen, two when they wrap on a
 * phone. The pills used to hard-code 128 / 184 against a 72px bar; a taller
 * bar would slide under them. The bar publishes its live height here and the
 * pills derive their `bottom` from it.
 */
import { useSyncExternalStore } from "react";

/** Gap between the viewport bottom and the bar (clears the zoom controls). */
export const BAR_BOTTOM = 72;
/** Breathing room between stacked pills. */
export const STACK_GAP = 12;

let barHeight = 0;
const listeners = new Set<() => void>();

export function publishBarHeight(h: number): void {
  const next = Math.max(0, Math.round(h));
  if (next === barHeight) return;
  barHeight = next;
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Current bar height (0 while hidden). */
export function useBarHeight(): number {
  return useSyncExternalStore(
    subscribe,
    () => barHeight,
    () => 0,
  );
}

/** `bottom` for the pill at `index` (0 = right above the bar). */
export function stackBottom(barH: number, index: number): number {
  const base = barH > 0 ? BAR_BOTTOM + barH + STACK_GAP : BAR_BOTTOM;
  return base + index * 56;
}
